// Shared salary-range helpers for job postings.
// INR amounts use the Indian Lakh/Crore convention (and the create/edit forms let the
// recruiter enter the range directly in Lakhs); other currencies use k/M.

export const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'INR', symbol: '\u20B9' },
  { code: 'EUR', symbol: '\u20AC' },
  { code: 'GBP', symbol: '\u00A3' },
  { code: 'AED', symbol: 'AED ' },
  { code: 'SGD', symbol: 'S$' },
];

export const SALARY_PERIODS = [
  { value: 'year', short: '/yr' },
  { value: 'month', short: '/mo' },
  { value: 'hour', short: '/hr' },
];

export const LAKH = 100000;
export const usesLakhInput = (currency) => currency === 'INR';

const symbolFor = (code) => (CURRENCIES.find((c) => c.code === code) || {}).symbol || '';
const shortPeriod = (v) => (SALARY_PERIODS.find((p) => p.value === v) || {}).short || '';

const trim2 = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, ''));

// Non-INR compact form (k / M).
const compact = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 1000 && num % 1000 === 0) return `${num / 1000}k`;
  if (num >= 10000) return `${Math.round(num / 1000)}k`;
  return num.toLocaleString();
};

// Indian compact form: Crore (≥1,00,00,000) / Lakh (≥1,00,000).
const compactINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 10000000) return `${trim2(num / 10000000)}Cr`;
  if (num >= 100000) return `${trim2(num / 100000)}L`;
  return num.toLocaleString('en-IN');
};

// Accepts a job-like object with snake_case salary fields.
export function formatSalary(job) {
  if (!job) return null;
  const inr = usesLakhInput(job.salary_currency);
  const min = inr ? compactINR(job.salary_min) : compact(job.salary_min);
  const max = inr ? compactINR(job.salary_max) : compact(job.salary_max);
  if (!min && !max) return null;
  const sym = symbolFor(job.salary_currency || 'USD');
  const per = shortPeriod(job.salary_period || 'year');
  let range;
  if (min && max) range = `${sym}${min}\u2013${sym}${max}`;
  else if (min) range = `From ${sym}${min}`;
  else range = `Up to ${sym}${max}`;
  return `${range} ${per}`.trim();
}

// ── Form helpers ─────────────────────────────────────────────────────────
// The Min/Max inputs are expressed in Lakhs when the currency is INR, and in absolute
// units otherwise. These convert between what the user types and the absolute value stored.

// Display input value -> absolute amount to store (null when empty/invalid).
export function toAbsolute(inputVal, currency) {
  if (inputVal === '' || inputVal == null) return null;
  const n = parseFloat(inputVal);
  if (!Number.isFinite(n) || n < 0) return null;
  return usesLakhInput(currency) ? Math.round(n * LAKH) : Math.round(n);
}

// Stored absolute amount -> value to show in the input.
export function toInputUnit(absolute, currency) {
  const n = Number(absolute);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (usesLakhInput(currency)) {
    const l = n / LAKH;
    return Number.isInteger(l) ? String(l) : String(parseFloat(l.toFixed(2)));
  }
  return String(Math.round(n));
}

// Live hint under the inputs, e.g. "\u20B95,00,000".
export function inputHint(inputVal, currency) {
  const abs = toAbsolute(inputVal, currency);
  if (abs == null) return '';
  const locale = usesLakhInput(currency) ? 'en-IN' : 'en-US';
  return `${symbolFor(currency)}${abs.toLocaleString(locale)}`;
}
